import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileDown, Lock } from "lucide-react"
import { useRef, useState } from "react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { pdfDeLosGastos } from "@/componentes/reportes/pdfGastos"
import { descargar, logoComoPng } from "@/componentes/reportes/pdfReporte"
import { type Gastos, nombreDelMes } from "@/componentes/reportes/tiposGastos"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * El reporte semanal de gastos administrativos, desde el enlace del aviso de los lunes
 * (`/g/<token>`, 13/10). **Sin sesión, sin menú y sin poder tocar nada**, con el aspecto
 * del reporte de cuentas por pagar.
 *
 * ⚠ **La semana cuenta PAGOS y el mes cuenta FACTURAS**, y la página lo dice: son dos
 * preguntas —qué salió del banco la semana pasada, y cuánto se ha gastado este mes—. El
 * mes sale de las mismas funciones que el informe de gastos y el presupuesto.
 *
 * ⚠ **Es la FOTO del lunes**: lo que se pague después se ve en la bandeja de órdenes.
 */

const usd = (t: string) => `USD ${monto(t)}`

const TIPO: Record<string, string> = { FIJO: "Fijo", VARIABLE: "Variable" }

export function GastosPublica() {
  const { token = "" } = useParams()
  const marca = useRef<HTMLDivElement>(null)
  const [generando, setGenerando] = useState(false)
  const [fallo, setFallo] = useState(false)
  const consulta = useQuery({
    queryKey: ["publico", "gastos", token],
    queryFn: () => api.get<Gastos>(`/api/v1/publico/gastos/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado. El del lunes trae el reporte nuevo.
        </p>
      </div>
    )
  }

  const g = consulta.data
  const { semana: s, mes: m, presupuesto: p } = g
  const tenue = { color: "var(--fg-muted)" }
  const borde = { borderColor: "var(--border)" }
  const rojo = { color: "var(--destructive)" }
  const negativo = (t: string) => (t.startsWith("-") ? rojo : undefined)
  const sinConvertir = [...s.sin_convertir, ...m.sin_convertir]
  const caja = "rounded-[var(--radius-control)] border p-3"
  const cifra = "whitespace-nowrap py-1 text-right tabular-nums"

  async function exportar() {
    setGenerando(true)
    setFallo(false)
    try {
      const logo = await logoComoPng(
        marca.current?.querySelector("svg") ?? null,
        "rgb(122, 27, 56)",
      )
      descargar(
        await pdfDeLosGastos(g, logo),
        `Gastos administrativos ${comoFecha(s.desde).replaceAll("/", "-")}.pdf`,
      )
    } catch {
      setFallo(true)
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="hoja-reporte mx-auto max-w-6xl p-4 sm:p-8">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <p
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
          style={{ ...borde, ...tenue }}
        >
          <Lock size={12} aria-hidden={true} />
          Vista de sólo lectura. El enlace caduca el {comoFecha(g.expira_en)}.
        </p>
        <button
          type="button"
          onClick={exportar}
          disabled={generando}
          className="foco-cristal inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-control)] border px-4 text-sm font-medium disabled:opacity-60"
          style={borde}
        >
          <FileDown size={16} aria-hidden={true} />
          {generando ? "Generando…" : "Exportar PDF"}
        </button>
        {fallo && (
          <p role="alert" className="w-full text-xs" style={rojo}>
            No se pudo generar el PDF. Vuelve a intentarlo.
          </p>
        )}
      </div>

      <header className="mb-5 flex items-center justify-between gap-4 border-b pb-3" style={borde}>
        <div ref={marca} className="shrink-0" style={{ color: "var(--fg)" }}>
          <LogoSetieca titulo={g.empresa_nombre} className="h-12 w-auto sm:h-14" />
        </div>
        <div className="min-w-0 text-right">
          <p className="text-sm font-semibold uppercase sm:text-base">{g.empresa_nombre}</p>
          {g.empresa_rif ? (
            <p className="text-xs" style={tenue}>
              RIF {g.empresa_rif}
            </p>
          ) : null}
        </div>
      </header>

      <p className="text-xs" style={tenue}>
        Generado el {comoFechaHora(g.generado_en)} · montos en dólares
      </p>
      <h1 className="mt-1 text-xl font-semibold">{g.titulo}</h1>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div className={caja} style={borde}>
          <dt style={tenue}>Pagado la semana pasada</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{usd(s.total)}</dd>
          <dd className="text-xs" style={tenue}>
            {s.pagos.length} pago(s)
          </dd>
        </div>
        <div className={caja} style={borde}>
          <dt style={tenue}>
            Ejecutado en {nombreDelMes(m.anio, m.mes)} al {comoFecha(m.hasta)}
          </dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{usd(m.ejecutado)}</dd>
          <dd className="text-xs tabular-nums" style={tenue}>
            Fijos {usd(m.fijos)} · Variables {usd(m.variables)}
            {Number(m.sin_clasificar) > 0 ? ` · Sin clasificar ${usd(m.sin_clasificar)}` : ""}
          </dd>
        </div>
        <div className={caja} style={borde}>
          <dt style={tenue}>Pendiente por ejecutar</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{usd(m.pendiente)}</dd>
          <dd className="text-xs" style={tenue}>
            {m.ordenes_pendientes} orden(es) aprobadas o en aprobación
          </dd>
        </div>
        <div className={caja} style={borde}>
          <dt style={tenue}>Presupuesto disponible</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums" style={negativo(p.disponible)}>
            {usd(p.disponible)}
          </dd>
          <dd className="text-xs tabular-nums" style={tenue}>
            Tope {usd(p.tope)} · usado {usd(p.usado)} · fuera de partida {usd(p.fuera_de_partida)}
          </dd>
        </div>
      </dl>

      <section className="grupo-reporte mt-8">
        <h2 className="border-b pb-1 text-base font-semibold" style={borde}>
          Pagos de la semana pasada ({comoFecha(s.desde)} al {comoFecha(s.hasta)})
        </h2>
        {s.pagos.length === 0 ? (
          <p className="mt-2 text-sm" style={tenue}>
            No hubo pagos esa semana.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="tabla-apilable mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs" style={tenue}>
                  <th className="py-1 pr-3 font-medium">Orden</th>
                  <th className="py-1 pr-3 font-medium">Fecha</th>
                  <th className="py-1 pr-3 font-medium">Proveedor</th>
                  <th className="py-1 pr-3 font-medium">Concepto</th>
                  <th className="py-1 text-right font-medium">Monto</th>
                </tr>
              </thead>
              <tbody>
                {s.pagos.map((x) => (
                  <tr
                    key={`${x.codigo}-${x.fecha}-${x.monto}`}
                    className="border-t align-top"
                    style={borde}
                  >
                    <td data-rotulo="Orden" className="py-1 pr-3 font-medium tabular-nums">
                      {x.codigo}
                    </td>
                    <td data-rotulo="Fecha" className="whitespace-nowrap py-1 pr-3 tabular-nums">
                      {comoFecha(x.fecha)}
                    </td>
                    <td data-rotulo="Proveedor" className="py-1 pr-3">
                      {x.proveedor}
                    </td>
                    <td data-rotulo="Concepto" className="py-1 pr-3">
                      {x.concepto}
                    </td>
                    <td data-rotulo="Monto" className={`${cifra} font-medium`}>
                      {usd(x.monto)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="grupo-reporte mt-8">
        <h2 className="border-b pb-1 text-base font-semibold" style={borde}>
          Presupuesto del mes por partida
        </h2>
        {p.partidas.length === 0 ? (
          <p className="mt-2 text-sm" style={tenue}>
            No hay partidas de presupuesto.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="tabla-apilable mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs" style={tenue}>
                  <th className="py-1 pr-3 font-medium">Partida</th>
                  <th className="py-1 pr-3 text-right font-medium">Tope</th>
                  <th className="py-1 pr-3 text-right font-medium">Ejecutado</th>
                  <th className="py-1 pr-3 text-right font-medium">Comprometido</th>
                  <th className="py-1 pr-3 text-right font-medium">Disponible</th>
                  <th className="py-1 text-right font-medium">Uso</th>
                </tr>
              </thead>
              <tbody>
                {p.partidas.map((x) => (
                  <tr key={x.categoria} className="border-t align-top" style={borde}>
                    <td data-rotulo="Partida" className="py-1 pr-3 font-medium">
                      {x.categoria}
                    </td>
                    <td data-rotulo="Tope" className={`${cifra} pr-3`}>
                      {usd(x.tope)}
                    </td>
                    <td data-rotulo="Ejecutado" className={`${cifra} pr-3`}>
                      {usd(x.ejecutado)}
                    </td>
                    <td data-rotulo="Comprometido" className={`${cifra} pr-3`}>
                      {usd(x.comprometido)}
                    </td>
                    <td
                      data-rotulo="Disponible"
                      className={`${cifra} pr-3`}
                      style={negativo(x.disponible)}
                    >
                      {usd(x.disponible)}
                    </td>
                    <td
                      data-rotulo="Uso"
                      className={`${cifra} font-medium`}
                      style={x.alerta ? rojo : undefined}
                    >
                      {x.porcentaje} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="grupo-reporte mt-8">
        <h2 className="border-b pb-1 text-base font-semibold" style={borde}>
          Gasto de {nombreDelMes(m.anio, m.mes)} por categoría
        </h2>
        {m.por_categoria.length === 0 ? (
          <p className="mt-2 text-sm" style={tenue}>
            Todavía no hay gasto ejecutado este mes.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="tabla-apilable mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-xs" style={tenue}>
                  <th className="py-1 pr-3 font-medium">Categoría</th>
                  <th className="py-1 pr-3 font-medium">Tipo</th>
                  <th className="py-1 pr-3 text-right font-medium">Órdenes</th>
                  <th className="py-1 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {m.por_categoria.map((x) => (
                  <tr key={x.categoria} className="border-t align-top" style={borde}>
                    <td data-rotulo="Categoría" className="py-1 pr-3 font-medium">
                      {x.categoria}
                    </td>
                    <td data-rotulo="Tipo" className="py-1 pr-3">
                      {(x.tipo && TIPO[x.tipo]) ?? "—"}
                    </td>
                    <td data-rotulo="Órdenes" className={`${cifra} pr-3`}>
                      {x.ordenes}
                    </td>
                    <td data-rotulo="Total" className={`${cifra} font-medium`}>
                      {usd(x.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {sinConvertir.length > 0 ? (
        <section className="mt-6 border-t pt-3 text-sm" style={borde}>
          <h2 className="font-semibold">Sin convertir a dólares</h2>
          <ul className="mt-1 list-disc pl-5" style={tenue}>
            {sinConvertir.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
